import { Component } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { GlobalPolicyRulePayload, PolicyRule, PolicyService } from '../../partner/policy-controls/policy.service';
import { MasterdataService } from '../../../services/masterdata.service';
import { TherapyVerticals } from 'projects/app-core/src/models/TherapyVerticals';
import { TherapyConcerns } from 'projects/app-core/src/models/TherapyConcerns';
import { TherapistExpertLevel } from 'projects/app-core/src/models/TherapistExpertLevel';
import { TherapistService } from '../../../services/therapist.service';
import { IAnyoTherapist } from '../../../models/IAnyoTherapist';

// ── Shared UI interfaces (mirrors policy-controls) ─────────────────────────────

export interface ConditionRow {
  fact: string;
  operator: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  params?: Record<string, unknown>;
}

export interface GlobalRuleUI {
  id?: string;
  name: string;
  priority: number;
  active: boolean;
  action: string;
  scope: { verticalId?: string[]; concernId?: string[]; expertLevelId?: string; therapistIds?: string[] };
  conditionMode: 'always' | 'when';
  condLogic: 'all' | 'any';
  condRows: ConditionRow[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  outputs: Record<string, any>;
  expanded: boolean;
  saving: boolean;
  // Precomputed header summaries — updated on load and on collapse to avoid
  // calling functions with O(n) array ops on every change-detection tick.
  _scopeSummary: string;
  _condSummary: string;
  _thenSummary: string;
}

// ── Facts / outputs / operators (mirrors policy-vue.html) ─────────────────────

const FACTS: Record<string, { value: string; label: string; type: 'number' | 'bool'; description?: string; hasParams?: boolean }[]> = {
  'Session Booking': [
    { value: 'sessionsInLastNDays', label: 'Sessions booked in last N days (this vertical + concern)', type: 'number', hasParams: true },
    { value: 'totalSessionsInLastNDays', label: 'Sessions booked in last N days (all verticals)', type: 'number', hasParams: true },
    { value: 'sessionsConsumedInPeriod', label: 'Sessions booked (this concern)', type: 'number' },
    { value: 'sessionsConsumedInPeriodForVertical', label: 'Sessions booked (this vertical)', type: 'number' },
    { value: 'totalSessionsConsumedInPeriod', label: 'Sessions booked (all verticals)', type: 'number' },
    { value: 'sessionsConsumedInPeriodForScopeVerticals', label: 'Sessions booked (selected verticals combined)', type: 'number' },
    { value: 'maxSessionsConsumedInPeriodAcrossScopeVerticals', label: 'Sessions booked (max across any selected vertical)', type: 'number' },
    { value: 'sessionsConsumedInPeriodForScopeConcerns', label: 'Sessions booked (selected concerns combined)', type: 'number' },
    { value: 'maxSessionsConsumedInPeriodAcrossScopeConcerns', label: 'Sessions booked (max across any selected concern)', type: 'number' },
    { value: 'sessionsConsumedInPeriodForScopeVerticalAndConcern', label: 'Sessions booked (selected verticals + concerns combined)', type: 'number' },
    { value: 'lastSessionDateForScopeVerticals', label: 'Last session date (selected verticals)', type: 'number' },
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
    { value: 'hasHadSessionWithTherapist', label: 'Has had session with this therapist', type: 'bool' },
    { value: 'sessionsWithThisTherapist', label: 'Session count with this expert', type: 'number' },
  ],
  'Event Registration': [
    { value: 'eventsConsumedInPeriod', label: 'Events registered this period', type: 'number' },
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
  ],
  'Diagnostic Order': [
    { value: 'diagnosticsOrdersConsumedInPeriod', label: 'Diagnostic orders this period', type: 'number' },
    { value: 'diagnosticsSpendInPeriod', label: 'Diagnostic spend (INR)', type: 'number' },
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
  ],
};

const OUTPUTS: Record<
  string,
  { key: string; label: string; type: 'select' | 'number' | 'text'; options?: { v: string; l: string }[] }[]
> = {
  'Session Booking': [
    {
      key: 'payer', label: 'Who pays', type: 'select',
      options: [{ v: 'company', l: 'Company' }, { v: 'user', l: 'Employee / user' }],
    },
    {
      key: 'sessionMinutes', label: 'Session duration', type: 'select',
      options: [{ v: '20', l: '20 min' }, { v: '30', l: '30 min' }, { v: '45', l: '45 min' }, { v: '60', l: '60 min' }, { v: '90', l: '90 min' }],
    },
    {
      key: 'sessionAllowed', label: 'Allow booking', type: 'select',
      options: [{ v: 'true', l: 'Yes — allow' }, { v: 'false', l: 'No — block' }],
    },
    { key: 'cooldown', label: 'Min. days between sessions', type: 'number' },
    { key: 'blockReason', label: 'Block reason (shown to user)', type: 'text' },
    // Self-pay pricing — only shown when payer='user'. GST and markup RATE come from
    // Contract Settings (partner-level); this rule only picks the pricing MODE and,
    // for slab mode, the markup VALUE on top of it.
    {
      key: 'selfPayPricingMode', label: 'Self-pay pricing', type: 'select',
      options: [
        { v: 'slab', l: 'Therapist slab pricing + markup' },
        { v: 'flat', l: 'Flat amount' },
        { v: 'flatSlab', l: 'Flat amount by session duration' },
      ],
    },
    {
      key: 'selfPaySlabSource', label: 'Slab pricing source', type: 'select',
      options: [{ v: 'corporate', l: 'Corporate slab' }, { v: 'retail', l: 'Retail slab' }],
    },
    {
      key: 'selfPayMarkupType', label: 'Markup type', type: 'select',
      options: [{ v: 'percent', l: 'Percent (%)' }, { v: 'fixed', l: 'Fixed amount (₹)' }],
    },
    { key: 'selfPayMarkupValue', label: 'Markup value', type: 'number' },
    { key: 'selfPayFlatAmount', label: 'Flat amount (₹)', type: 'number' },
  ],
  'Event Registration': [
    {
      key: 'payer', label: 'Who pays', type: 'select',
      options: [{ v: 'company', l: 'Company' }, { v: 'user', l: 'Employee / user' }],
    },
    {
      key: 'eventAllowed', label: 'Allow registration', type: 'select',
      options: [{ v: 'true', l: 'Yes — allow' }, { v: 'false', l: 'No — block' }],
    },
  ],
  'Diagnostic Order': [
    {
      key: 'payer', label: 'Who pays', type: 'select',
      options: [{ v: 'company', l: 'Company' }, { v: 'user', l: 'Employee / user' }],
    },
    {
      key: 'diagAllowed', label: 'Allow ordering', type: 'select',
      options: [{ v: 'true', l: 'Yes — allow' }, { v: 'false', l: 'No — block' }],
    },
  ],
};

// Self-pay pricing fields — only relevant on a Session Booking rule whose payer='user'.
// Within that, slab-only fields only apply to selfPayPricingMode='slab', and the flat-only
// field only applies to 'flat'. GST and markup rate are partner-level (Contract Settings) —
// this rule only picks the mode + (for slab) the markup value on top of it.
// selfPayFlatSlabPricing (flatSlab mode's price-by-duration table) is NOT in this list —
// it's array-valued and rendered by its own bespoke row-editor block, not the generic
// single-value OUTPUTS renderer these constants gate.
const SELF_PAY_SLAB_ONLY_FIELDS = ['selfPaySlabSource', 'selfPayMarkupType', 'selfPayMarkupValue'];
const SELF_PAY_FLAT_ONLY_FIELDS = ['selfPayFlatAmount'];
const SELF_PAY_PRICING_FIELDS = ['selfPayPricingMode', ...SELF_PAY_SLAB_ONLY_FIELDS, ...SELF_PAY_FLAT_ONLY_FIELDS];

const NUM_OPS = [
  { value: 'equal', label: '=' },
  { value: 'notEqual', label: '≠' },
  { value: 'lessThan', label: '<' },
  { value: 'greaterThan', label: '>' },
  { value: 'lessThanInclusive', label: '≤' },
  { value: 'greaterThanInclusive', label: '≥' },
];

// ── Conversion helpers ─────────────────────────────────────────────────────────

function parseConditions(raw: unknown): {
  conditionMode: 'always' | 'when';
  condLogic: 'all' | 'any';
  condRows: ConditionRow[];
} {
  const obj = raw as Record<string, unknown> | null | undefined;
  if (!obj) return { conditionMode: 'always', condLogic: 'all', condRows: [] };

  const allRows = (obj['all'] as unknown[]) ?? [];
  const anyRows = (obj['any'] as unknown[]) ?? [];
  const condLogic: 'all' | 'any' = anyRows.length > 0 && allRows.length === 0 ? 'any' : 'all';
  const rows = condLogic === 'any' ? anyRows : allRows;

  if (!rows.length) return { conditionMode: 'always', condLogic, condRows: [] };

  return {
    conditionMode: 'when',
    condLogic,
    condRows: rows.map((r) => {
      const row = r as Record<string, unknown>;
      return {
        fact: String(row['fact'] ?? ''),
        operator: String(row['operator'] ?? 'equal'),
        value: (row['value'] ?? '') as string | number | boolean,
        ...(row['params'] ? { params: row['params'] as Record<string, unknown> } : {}),
      };
    }),
  };
}

function buildConditions(
  conditionMode: 'always' | 'when',
  condLogic: 'all' | 'any',
  condRows: ConditionRow[],
): unknown {
  if (conditionMode === 'always' || !condRows.length) return { all: [] };
  const validRows = condRows.filter((r) => r.fact && r.fact.trim() !== '');
  if (!validRows.length) return { all: [] };
  return {
    [condLogic]: validRows.map((r) => ({
      fact: r.fact,
      operator: r.operator,
      value: r.value,
      ...(r.params && Object.keys(r.params).length > 0 ? { params: r.params } : {}),
    })),
  };
}

function coerceScopeToArrays(scope: PolicyRule['scope'] | undefined): GlobalRuleUI['scope'] {
  const s = scope || {};
  return {
    ...s,
    verticalId: Array.isArray(s.verticalId) ? s.verticalId : s.verticalId ? [(s.verticalId as unknown as string)] : undefined,
    concernId: Array.isArray(s.concernId) ? s.concernId : s.concernId ? [(s.concernId as unknown as string)] : undefined,
    therapistIds: Array.isArray(s.therapistIds) ? s.therapistIds : s.therapistIds ? [(s.therapistIds as unknown as string)] : undefined,
  };
}

function toGlobalRuleUI(rule: PolicyRule): GlobalRuleUI {
  const { conditionMode, condLogic, condRows } = parseConditions(rule.conditions);
  return {
    id: rule.id,
    name: rule.name,
    priority: rule.priority,
    active: rule.active,
    action: rule.action || 'Session Booking',
    scope: coerceScopeToArrays(rule.scope),
    conditionMode,
    condLogic,
    condRows,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    outputs: (rule.outputs as Record<string, any>) || {},
    expanded: false,
    saving: false,
    _scopeSummary: '',
    _condSummary: '',
    _thenSummary: '',
  };
}

function fromGlobalRuleUI(ui: GlobalRuleUI): GlobalPolicyRulePayload {
  const outputs = Object.fromEntries(
    Object.entries(ui.outputs).filter(([, v]) => v !== null && v !== undefined && v !== ''),
  );
  return {
    name: ui.name,
    priority: ui.priority,
    active: ui.active,
    action: ui.action,
    scope: ui.scope,
    conditions: buildConditions(ui.conditionMode, ui.condLogic, ui.condRows),
    outputs,
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-global-policy-rules',
  templateUrl: './global-policy-rules.component.html',
})
export class GlobalPolicyRulesComponent extends AnyoComponent {
  loading = false;
  rulesUI: GlobalRuleUI[] = [];

  readonly ACTIONS = ['Session Booking', 'Event Registration', 'Diagnostic Order'];
  readonly FACTS = FACTS;
  readonly OUTPUTS = OUTPUTS;
  readonly NUM_OPS = NUM_OPS;

  // Scope dropdowns
  verticals: TherapyVerticals[] = [];
  concerns: TherapyConcerns[] = [];
  expertLevels: TherapistExpertLevel[] = [];
  therapists: IAnyoTherapist[] = [];
  // O(1) lookup maps — built once after master data loads
  private verticalsMap = new Map<string, string>();
  private concernsMap = new Map<string, string>();
  private expertLevelsMap = new Map<string, string>();

  constructor(
    protected override auth: AuthService,
    private policyService: PolicyService,
    private masterdataService: MasterdataService,
    private therapistService: TherapistService,
    private message: NzMessageService,
  ) {
    super(auth);
  }

  async ready(): Promise<void> {
    this.loading = true;
    let pending = 3;
    const done = () => { if (--pending <= 0) { this.loading = false; this.recomputeAllSummaries(); } };
    this.masterdataService.masterDataList().subscribe({
      next: (data) => {
        this.verticals = data.therapyVerticals ?? [];
        this.concerns = data.therapyConcerns ?? [];
        this.expertLevels = data.therapistExpertLevel ?? [];
        this.verticalsMap = new Map(this.verticals.map(v => [v.m_id, v.data]));
        this.concernsMap = new Map(this.concerns.map(c => [c.m_id, c.data]));
        this.expertLevelsMap = new Map(this.expertLevels.map(e => [e.m_id, e.data]));
        done();
      },
      error: () => done(),
    });
    this.therapistService.therapistList().subscribe({
      next: (data) => { this.therapists = data; done(); },
      error: () => done(),
    });
    this.policyService.getGlobalPolicyRules().subscribe({
      next: (rules) => {
        this.rulesUI = rules.map(toGlobalRuleUI);
        done();
      },
      error: () => {
        this.message.error('Failed to load global rules');
        done();
      },
    });
  }

  therapistDisplayName(therapistId: string | undefined): string {
    if (!therapistId) return '';
    const t = this.therapists.find((x) => x._id === therapistId);
    return t ? `${t.title} ${t.firstName} ${t.lastName}`.trim() : `${therapistId} (not found)`;
  }

  private recomputeAllSummaries() {
    this.rulesUI.forEach(r => this.recomputeSummaries(r));
  }

  private recomputeSummaries(rule: GlobalRuleUI) {
    rule._scopeSummary = this.computeScopeSummary(rule);
    rule._condSummary = this.computeCondSummary(rule);
    rule._thenSummary = this.computeThenSummary(rule);
  }

  private computeScopeSummary(rule: GlobalRuleUI): string {
    const parts = [
      rule.scope.verticalId?.length
        ? rule.scope.verticalId.map(id => this.verticalsMap.get(id) ?? id).join(', ')
        : null,
      rule.scope.concernId?.length
        ? rule.scope.concernId.map(id => this.concernsMap.get(id) ?? id).join(', ')
        : null,
      rule.scope.expertLevelId
        ? (this.expertLevelsMap.get(rule.scope.expertLevelId) ?? rule.scope.expertLevelId)
        : null,
      rule.scope.therapistIds?.length
        ? rule.scope.therapistIds.map((id) => this.therapistDisplayName(id)).join(', ')
        : null,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'All';
  }

  private computeCondSummary(rule: GlobalRuleUI): string {
    if (rule.conditionMode === 'always' || !rule.condRows.length) return 'Always applies';
    const opMap: Record<string, string> = {
      equal: '=', notEqual: '≠', lessThan: '<', greaterThan: '>',
      lessThanInclusive: '≤', greaterThanInclusive: '≥',
    };
    const sep = rule.condLogic === 'any' ? ' OR ' : ' AND ';
    const parts = rule.condRows.slice(0, 2).map(c => {
      if (!c.fact) return '?';
      if (this.factType(rule.action, c.fact) === 'bool') return `${c.fact}=${c.value ? 'yes' : 'no'}`;
      return `${c.fact} ${opMap[c.operator] ?? c.operator} ${c.value}`;
    });
    return 'When ' + parts.join(sep) + (rule.condRows.length > 2 ? ' …' : '');
  }

  private computeThenSummary(rule: GlobalRuleUI): string {
    const outs = OUTPUTS[rule.action] ?? [];
    const set = outs.filter(o => rule.outputs[o.key] !== '' && rule.outputs[o.key] !== undefined && rule.outputs[o.key] !== null);
    if (!set.length) return '(no outputs)';
    const short: Record<string, string> = {
      payer: 'payer', sessionMinutes: 'duration', sessionAllowed: 'allowed',
      eventAllowed: 'allowed', diagAllowed: 'allowed', cooldown: 'cooldown', blockReason: 'reason',
    };
    return (
      set.slice(0, 3).map(o => {
        const label = short[o.key] ?? o.key;
        const val = o.key === 'blockReason' ? '✓' : rule.outputs[o.key];
        return `${label}=${val}`;
      }).join(' · ') + (set.length > 3 ? ` +${set.length - 3}` : '')
    );
  }

  addRule() {
    this.rulesUI.unshift({
      name: '',
      priority: 10,
      active: true,
      action: 'Session Booking',
      scope: {},
      conditionMode: 'always',
      condLogic: 'all',
      condRows: [],
      outputs: {},
      expanded: true,
      saving: false,
      _scopeSummary: 'All',
      _condSummary: 'Always applies',
      _thenSummary: '(no outputs)',
    });
  }

  toggleExpand(rule: GlobalRuleUI) {
    rule.expanded = !rule.expanded;
    if (!rule.expanded) this.recomputeSummaries(rule);
  }

  onActionChange(rule: GlobalRuleUI) {
    rule.condRows = [];
    rule.outputs = {};
  }

  addCond(rule: GlobalRuleUI) {
    rule.condRows.push({ fact: '', operator: 'equal', value: '' });
  }

  removeCond(rule: GlobalRuleUI, idx: number) { rule.condRows.splice(idx, 1); }

  onFactChange(rule: GlobalRuleUI, row: ConditionRow) {
    const meta = (FACTS[rule.action] ?? []).find((f) => f.value === row.fact);
    row.operator = 'equal';
    row.value = meta?.type === 'bool' ? false : '';
    row.params = meta?.hasParams ? { days: 7 } : undefined;
  }

  factType(action: string, fact: string): 'number' | 'bool' {
    return (FACTS[action] ?? []).find((f) => f.value === fact)?.type ?? 'number';
  }

  private resolveNames(ids: string[] | undefined, list: { m_id: string; data: string }[]): string {
    if (!ids?.length) return '';
    return ids.map((id) => list.find((x) => x.m_id === id)?.data ?? id).join(', ');
  }

  private factDescription(value: string, vNames: string, cNames: string): string {
    switch (value) {
      case 'sessionsConsumedInPeriod':
        return `Sessions in whichever concern is being booked now${cNames ? ' (' + cNames + ')' : ''}`;
      case 'sessionsConsumedInPeriodForVertical':
        return `Sessions in whichever vertical is being booked now${vNames ? ' (' + vNames + ')' : ''}`;
      case 'totalSessionsConsumedInPeriod':
        return 'Total sessions across all verticals';
      case 'sessionsConsumedInPeriodForScopeVerticals':
        return `Sum of sessions across ${vNames || 'selected verticals'} combined`;
      case 'maxSessionsConsumedInPeriodAcrossScopeVerticals':
        return `Highest session count from any single vertical among: ${vNames || 'selected verticals'}`;
      case 'sessionsConsumedInPeriodForScopeConcerns':
        return `Sum of sessions across ${cNames || 'selected concerns'} combined`;
      case 'maxSessionsConsumedInPeriodAcrossScopeConcerns':
        return `Highest session count from any single concern among: ${cNames || 'selected concerns'}`;
      case 'sessionsConsumedInPeriodForScopeVerticalAndConcern':
        return `Sessions matching both verticals (${vNames || '…'}) and concerns (${cNames || '…'})`;
      case 'lastSessionDateForScopeVerticals':
        return `Date of most recent session in any of: ${vNames || 'selected verticals'}`;
      default:
        return '';
    }
  }

  private _factsCache = new Map<string, { value: string; label: string; type: 'number' | 'bool'; description?: string; hasParams?: boolean }[]>();

  factsFor(action: string, rule?: GlobalRuleUI) {
    const all = FACTS[action] ?? [];
    if (!rule) return all;

    const cacheKey = `${action}|${(rule.scope?.verticalId ?? []).join(',')}|${(rule.scope?.concernId ?? []).join(',')}`;
    if (this._factsCache.has(cacheKey)) return this._factsCache.get(cacheKey)!;

    const hasMultiVerticals = (rule.scope?.verticalId?.length ?? 0) > 1;
    const hasMultiConcerns = (rule.scope?.concernId?.length ?? 0) > 1;
    const hasConcerns = (rule.scope?.concernId?.length ?? 0) > 0;
    const hasScopeArrays = hasMultiVerticals || hasMultiConcerns;

    const vNames = this.resolveNames(rule.scope?.verticalId, this.verticals);
    const cNames = this.resolveNames(rule.scope?.concernId, this.concerns);
    const withDesc = (f: typeof all[0]) => ({ ...f, description: this.factDescription(f.value, vNames, cNames) });

    const singleScopeFacts = ['sessionsConsumedInPeriod', 'sessionsConsumedInPeriodForVertical', 'totalSessionsConsumedInPeriod'];
    const multiScopeFacts = ['sessionsConsumedInPeriodForScopeVerticals', 'maxSessionsConsumedInPeriodAcrossScopeVerticals', 'sessionsConsumedInPeriodForScopeConcerns', 'maxSessionsConsumedInPeriodAcrossScopeConcerns', 'sessionsConsumedInPeriodForScopeVerticalAndConcern', 'lastSessionDateForScopeVerticals'];

    if (hasScopeArrays) {
      const result = all.filter((f) => {
        if (f.value === 'sessionsConsumedInPeriodForVertical') return hasMultiVerticals;
        if (f.value === 'sessionsConsumedInPeriod') return hasConcerns;
        if (f.value === 'totalSessionsConsumedInPeriod') return false;
        if (f.value === 'sessionsConsumedInPeriodForScopeVerticals') return hasMultiVerticals;
        if (f.value === 'maxSessionsConsumedInPeriodAcrossScopeVerticals') return hasMultiVerticals;
        if (f.value === 'sessionsConsumedInPeriodForScopeConcerns') return hasMultiConcerns;
        if (f.value === 'maxSessionsConsumedInPeriodAcrossScopeConcerns') return hasMultiConcerns;
        if (f.value === 'sessionsConsumedInPeriodForScopeVerticalAndConcern') return hasMultiVerticals && hasConcerns;
        if (f.value === 'lastSessionDateForScopeVerticals') return hasMultiVerticals;
        return true;
      }).map(withDesc);
      this._factsCache.set(cacheKey, result);
      return result;
    }

    let correctSessionFact: string;
    if (rule.scope?.concernId?.length) {
      correctSessionFact = 'sessionsConsumedInPeriod';
    } else if (rule.scope?.verticalId?.length) {
      correctSessionFact = 'sessionsConsumedInPeriodForVertical';
    } else {
      correctSessionFact = 'totalSessionsConsumedInPeriod';
    }

    const result = all
      .filter(
        (f) =>
          !multiScopeFacts.includes(f.value) &&
          (!singleScopeFacts.includes(f.value) || f.value === correctSessionFact),
      )
      .map(withDesc);
    this._factsCache.set(cacheKey, result);
    return result;
  }
  outputsFor(action: string) { return OUTPUTS[action] ?? []; }

  /** True when this output should be shown at all — self-pay pricing fields are hidden
   * (not just disabled) when they don't apply, to avoid cluttering every non-self-pay rule
   * with 5 irrelevant fields. */
  isFieldVisible(rule: GlobalRuleUI, out: { key: string }): boolean {
    if (!SELF_PAY_PRICING_FIELDS.includes(out.key)) return true;
    if (rule.outputs['payer'] !== 'user') return false;
    if (out.key === 'selfPayPricingMode') return true;
    const mode = rule.outputs['selfPayPricingMode'];
    if (SELF_PAY_SLAB_ONLY_FIELDS.includes(out.key)) return mode === 'slab';
    if (SELF_PAY_FLAT_ONLY_FIELDS.includes(out.key)) return mode === 'flat';
    return true;
  }

  /** Clears self-pay pricing fields that just became irrelevant — payer changed away from
   * 'user', or selfPayPricingMode changed away from the mode a field belongs to. */
  onSelfPayOutputChange(rule: GlobalRuleUI, changedKey: string): void {
    if (changedKey === 'payer' && rule.outputs['payer'] !== 'user') {
      for (const key of SELF_PAY_PRICING_FIELDS) rule.outputs[key] = '';
      rule.outputs['selfPayFlatSlabPricing'] = [];
      return;
    }
    if (changedKey === 'selfPayPricingMode') {
      const mode = rule.outputs['selfPayPricingMode'];
      if (mode !== 'slab') for (const key of SELF_PAY_SLAB_ONLY_FIELDS) rule.outputs[key] = '';
      if (mode !== 'flat') for (const key of SELF_PAY_FLAT_ONLY_FIELDS) rule.outputs[key] = '';
      if (mode !== 'flatSlab') rule.outputs['selfPayFlatSlabPricing'] = [];
    }
  }

  /** True when the flatSlab price-by-duration row editor should be shown for this rule. */
  showFlatSlabEditor(rule: GlobalRuleUI): boolean {
    return rule.outputs['payer'] === 'user' && rule.outputs['selfPayPricingMode'] === 'flatSlab';
  }

  flatSlabRows(rule: GlobalRuleUI): { minDuration: number | null; maxDuration: number | null; amount: number | null }[] {
    return (rule.outputs['selfPayFlatSlabPricing'] as { minDuration: number | null; maxDuration: number | null; amount: number | null }[]) ?? [];
  }

  addFlatSlabRow(rule: GlobalRuleUI): void {
    if (!Array.isArray(rule.outputs['selfPayFlatSlabPricing'])) {
      rule.outputs['selfPayFlatSlabPricing'] = [];
    }
    (rule.outputs['selfPayFlatSlabPricing'] as unknown[]).push({
      minDuration: null,
      maxDuration: null,
      amount: null,
    });
  }

  removeFlatSlabRow(rule: GlobalRuleUI, idx: number): void {
    (rule.outputs['selfPayFlatSlabPricing'] as unknown[])?.splice(idx, 1);
  }

  saveRule(rule: GlobalRuleUI) {
    if (!rule.name.trim()) { this.message.warning('Rule name is required'); return; }
    rule.saving = true;
    const payload = fromGlobalRuleUI(rule);
    const call = rule.id
      ? this.policyService.updateGlobalPolicyRule(rule.id, payload)
      : this.policyService.createGlobalPolicyRule(payload);
    call.subscribe({
      next: () => {
        this.message.success('Global rule saved');
        rule.saving = false;
        rule.expanded = false;
        this.ready();
      },
      error: () => { this.message.error('Failed to save global rule'); rule.saving = false; },
    });
  }

  deleteRule(rule: GlobalRuleUI) {
    if (!rule.id) { this.rulesUI = this.rulesUI.filter((r) => r !== rule); return; }
    this.policyService.deleteGlobalPolicyRule(rule.id).subscribe({
      next: () => {
        this.message.success('Global rule deleted');
        this.rulesUI = this.rulesUI.filter((r) => r !== rule);
      },
      error: () => this.message.error('Failed to delete global rule'),
    });
  }

  onScopeChange(rule: GlobalRuleUI) {
    this._factsCache.clear();
    this.recomputeSummaries(rule);
  }

  concernsForRule(rule: GlobalRuleUI) {
    if (!rule.scope?.verticalId?.length) return this.concerns;
    const verticalSet = new Set(rule.scope.verticalId);
    return this.concerns.filter((c) => c.verticalId?.some((vid) => verticalSet.has(vid)));
  }

  get activeRulesCount() { return this.rulesUI.filter((r) => r.active).length; }
}
